export const DEFAULT_OPTIONS = {
  fg_color: '#0f172a',
  bg_color: '#ffffff',
  module_width: 0.3,
  module_height: 20,
  scale: 10,
  border: 4,
  quiet_zone: 6.5,
  font_size: 10,
  font_bold: false,
  font_family: 'sans',
  scale_text: true,
  show_text: true,
  error_correction: 'M',
};

// Selectable font families for the human-readable barcode caption.
// Each maps to a backend font family id (see barcode_service.FONT_FAMILIES).
export const FONT_FAMILY_OPTIONS = [
  { id: 'sans', key: 'font_sans' },
  { id: 'serif', key: 'font_serif' },
  { id: 'mono', key: 'font_mono' },
  { id: 'narrow', key: 'font_narrow' },
];

export function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function whenImagesReady(doc, cb) {
  const imgs = Array.prototype.slice.call(doc.images || []);
  Promise.all(
    imgs.map((img) => (img.complete ? Promise.resolve() : new Promise((r) => { img.onload = img.onerror = r; })))
  ).then(cb);
}

export function printImages(items, title = 'Barcodes') {
  // items: [{ data_url, label }]
  const list = (Array.isArray(items) ? items : [items]).filter(Boolean);
  if (!list.length) return;
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return;
  const doc = win.document;
  doc.title = title;

  const style = doc.createElement('style');
  style.textContent = `
    * { box-sizing: border-box; }
    body { margin: 0; padding: 16px; font-family: 'Work Sans', Arial, sans-serif; background:#fff; }
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px; }
    .card { margin: 0; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; text-align: center; page-break-inside: avoid; }
    .card img { max-width: 100%; height: auto; }
    figcaption { margin-top: 8px; font-size: 12px; color: #334155; word-break: break-all; font-family: ui-monospace, monospace; }
    @media print { body { padding: 0; } .card { border: none; } }`;
  doc.head.appendChild(style);

  const grid = doc.createElement('div');
  grid.className = 'grid';
  list.forEach((it) => {
    const fig = doc.createElement('figure');
    fig.className = 'card';
    const img = doc.createElement('img');
    img.src = it.data_url; // data: URL only
    fig.appendChild(img);
    if (it.label) {
      const cap = doc.createElement('figcaption');
      cap.textContent = String(it.label); // safe: no HTML injection
      fig.appendChild(cap);
    }
    grid.appendChild(fig);
  });
  doc.body.appendChild(grid);

  whenImagesReady(doc, () => setTimeout(() => { win.focus(); win.print(); }, 150));
}

export function printWifiCard(dataUrl, opts = {}) {
  // Printable "Guest Wi-Fi" card with QR + network name (+ optional password).
  // User values are inserted via textContent only (no HTML injection).
  const {
    title = 'Guest Wi-Fi',
    networkLabel = 'Network',
    passwordLabel = 'Password',
    instruction = 'Scan the QR code to connect.',
    ssid = '',
    password = '',
  } = opts;
  if (!dataUrl) return;
  const win = window.open('', '_blank', 'width=520,height=760');
  if (!win) return;
  const doc = win.document;
  doc.title = title;

  const style = doc.createElement('style');
  style.textContent = `
    @page { size: A6; margin: 10mm; }
    * { box-sizing: border-box; }
    body { margin: 0; padding: 24px; font-family: 'Work Sans', Arial, sans-serif; background:#fff;
           display:flex; align-items:center; justify-content:center; }
    .card { width: 320px; border: 2px solid #0f766e; border-radius: 18px; padding: 24px; text-align:center; }
    .title { font-size: 20px; font-weight: 800; color:#0f766e; margin:0 0 4px; }
    .instr { font-size: 12px; color:#475569; margin:0 0 16px; }
    .qr { width: 200px; height: 200px; margin: 0 auto 16px; }
    .qr img { width:100%; height:100%; image-rendering: pixelated; }
    .row { display:flex; justify-content:space-between; gap:12px; border-top:1px dashed #cbd5e1; padding:9px 2px; font-size:13px; }
    .row:last-child { border-bottom:1px dashed #cbd5e1; }
    .lbl { color:#64748b; }
    .val { font-weight:700; color:#0f172a; word-break:break-all; text-align:right; font-family: ui-monospace, 'Courier New', monospace; }
    @media print { body { padding: 0; } }`;
  doc.head.appendChild(style);

  const card = doc.createElement('div');
  card.className = 'card';

  const h1 = doc.createElement('div');
  h1.className = 'title';
  h1.textContent = String(title);
  card.appendChild(h1);

  const instr = doc.createElement('div');
  instr.className = 'instr';
  instr.textContent = String(instruction);
  card.appendChild(instr);

  const qrWrap = doc.createElement('div');
  qrWrap.className = 'qr';
  const img = doc.createElement('img');
  img.src = dataUrl; // data: URL only
  qrWrap.appendChild(img);
  card.appendChild(qrWrap);

  const makeRow = (label, val) => {
    const row = doc.createElement('div');
    row.className = 'row';
    const l = doc.createElement('span');
    l.className = 'lbl';
    l.textContent = String(label);
    const v = doc.createElement('span');
    v.className = 'val';
    v.textContent = String(val);
    row.appendChild(l);
    row.appendChild(v);
    return row;
  };

  card.appendChild(makeRow(networkLabel, ssid));
  if (password) card.appendChild(makeRow(passwordLabel, password));

  doc.body.appendChild(card);

  whenImagesReady(doc, () => setTimeout(() => { win.focus(); win.print(); }, 150));
}


export const LABEL_PRESETS = {
  '8': { cols: 2, rows: 4, label: '8 / A4 (2 × 4)' },
  '14': { cols: 2, rows: 7, label: '14 / A4 (2 × 7)' },
  '21': { cols: 3, rows: 7, label: '21 / A4 (3 × 7)' },
  '24': { cols: 3, rows: 8, label: '24 / A4 (3 × 8)' },
  '40': { cols: 4, rows: 10, label: '40 / A4 (4 × 10)' },
  '65': { cols: 5, rows: 13, label: '65 / A4 (5 × 13)' },
};

export function expandLabels(results) {
  // results: [{ status, value, caption, quantity, data_url }]
  const out = [];
  (results || []).forEach((r) => {
    if (r.status !== 'success') return;
    const qty = Math.max(1, Math.min(200, r.quantity || 1));
    for (let i = 0; i < qty; i++) out.push({ data_url: r.data_url, caption: r.caption, value: r.value });
  });
  return out;
}

export function printLabelSheet(labels, opts = {}) {
  const { cols = 3, rows = 8, margin = 8, gap = 2, showBorder = true, showCaption = true, exact = null, offsetX = 0, offsetY = 0 } = opts;
  if (!labels || !labels.length) return;
  const useExact = !!(exact && exact.w && exact.h && exact.dx && exact.dy);
  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) return;
  const doc = win.document;
  doc.title = 'Etiketten A4';

  const style = doc.createElement('style');
  if (useExact) {
    // Pixel-accurate placement: absolute mm positions straight from the template.
    const gCols = exact.cols || cols;
    const gRows = exact.rows || rows;
    const perPage = gCols * gRows;
    const pages = Math.ceil(labels.length / perPage);
    style.textContent = `
      @page { size: A4 portrait; margin: 0; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #fff; font-family: 'Work Sans', Arial, sans-serif; }
      .page { position: relative; width: 210mm; height: 297mm; page-break-after: always; overflow: hidden; }
      .page:last-child { page-break-after: auto; }
      .label { position: absolute; border: ${showBorder ? '0.3mm dashed #94a3b8' : 'none'}; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 1mm; overflow: hidden; }
      .label img { max-width: 100%; max-height: ${showCaption ? '72%' : '92%'}; object-fit: contain; }
      .cap { margin-top: 0.8mm; font-size: 7pt; line-height: 1.05; text-align: center; word-break: break-all; font-family: ui-monospace, monospace; color: #0f172a; }
      @media screen { body { padding: 10mm; background: #eef1f4; } .page { background:#fff; box-shadow:0 2px 12px rgba(0,0,0,.15); margin:0 auto 10mm; } }`;
    doc.head.appendChild(style);
    for (let p = 0; p < pages; p++) {
      const page = doc.createElement('div');
      page.className = 'page';
      const slice = labels.slice(p * perPage, (p + 1) * perPage);
      slice.forEach((l, idx) => {
        if (!l) return; // empty start slot on partially-used sheet
        const r = Math.floor(idx / gCols);
        const c = idx % gCols;
        const cell = doc.createElement('div');
        cell.className = 'label';
        cell.style.left = `${exact.x0 + c * exact.dx + offsetX}mm`;
        cell.style.top = `${exact.y0 + r * exact.dy + offsetY}mm`;
        cell.style.width = `${exact.w}mm`;
        cell.style.height = `${exact.h}mm`;
        const img = doc.createElement('img');
        img.src = l.data_url;
        cell.appendChild(img);
        const capText = l.caption || l.value;
        if (showCaption && capText) {
          const cap = doc.createElement('div');
          cap.className = 'cap';
          cap.textContent = String(capText);
          cell.appendChild(cap);
        }
        page.appendChild(cell);
      });
      doc.body.appendChild(page);
    }
    whenImagesReady(doc, () => setTimeout(() => { win.focus(); win.print(); }, 200));
    return;
  }

  const A4_H = 297;
  const rowH = ((A4_H - 2 * margin) - (rows - 1) * gap) / rows;
  doc.title = 'Etiketten A4';

  style.textContent = `
    @page { size: A4 portrait; margin: ${margin}mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; font-family: 'Work Sans', Arial, sans-serif; }
    .sheet { display: grid; grid-template-columns: repeat(${cols}, 1fr); grid-auto-rows: ${rowH}mm; gap: ${gap}mm; transform: translate(${offsetX}mm, ${offsetY}mm); }
    .label { border: ${showBorder ? '0.3mm dashed #94a3b8' : 'none'}; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 1.5mm; overflow: hidden; page-break-inside: avoid; break-inside: avoid; }
    .label img { max-width: 100%; max-height: ${showCaption ? '72%' : '90%'}; object-fit: contain; }
    .cap { margin-top: 1mm; font-size: 8pt; line-height: 1.1; text-align: center; word-break: break-all; font-family: ui-monospace, monospace; color: #0f172a; }
    @media screen { body { padding: 10mm; background: #eef1f4; } .sheet { background:#fff; padding:${margin}mm; box-shadow:0 2px 12px rgba(0,0,0,.15); width:210mm; margin:0 auto; } }`;
  doc.head.appendChild(style);

  const sheet = doc.createElement('div');
  sheet.className = 'sheet';
  labels.forEach((l) => {
    const cell = doc.createElement('div');
    cell.className = 'label';
    if (!l) { cell.style.border = 'none'; sheet.appendChild(cell); return; } // empty start slot
    const img = doc.createElement('img');
    img.src = l.data_url;
    cell.appendChild(img);
    const capText = l.caption || l.value;
    if (showCaption && capText) {
      const cap = doc.createElement('div');
      cap.className = 'cap';
      cap.textContent = String(capText); // safe
      cell.appendChild(cap);
    }
    sheet.appendChild(cell);
  });
  doc.body.appendChild(sheet);

  whenImagesReady(doc, () => setTimeout(() => { win.focus(); win.print(); }, 200));
}

// Prints a calibration test sheet: one marker per label cell (corner ticks +
// crosshair + cell index) using the CURRENT layout and the CURRENT print offset.
// The user prints this on a real label sheet to see how far the printer is off,
// then adjusts the offset until the crosshairs sit in the centre of each label.
export function printCalibrationSheet(opts = {}) {
  const { cols = 3, rows = 8, margin = 8, gap = 2, exact = null, offsetX = 0, offsetY = 0, lang = 'de' } = opts;
  const useExact = !!(exact && exact.w && exact.h && exact.dx && exact.dy);
  const gCols = useExact ? (exact.cols || cols) : cols;
  const gRows = useExact ? (exact.rows || rows) : rows;
  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) return;
  const doc = win.document;
  doc.title = lang === 'en' ? 'Calibration test sheet' : 'Kalibrierungs-Testseite';

  const PW = 210, PH = 297;
  const cellW = useExact ? exact.w : ((PW - 2 * margin) - (gCols - 1) * gap) / gCols;
  const cellH = useExact ? exact.h : ((PH - 2 * margin) - (gRows - 1) * gap) / gRows;

  const style = doc.createElement('style');
  style.textContent = `
    @page { size: A4 portrait; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; font-family: 'Work Sans', Arial, sans-serif; color: #0f172a; }
    .page { position: relative; width: 210mm; height: 297mm; overflow: hidden; }
    .cell { position: absolute; border: 0.25mm dashed #cbd5e1; }
    .tick { position: absolute; background: #0f172a; }
    .t-h { height: 0.3mm; width: 4mm; }
    .t-v { width: 0.3mm; height: 4mm; }
    .cross-h { position: absolute; left: 50%; top: 50%; width: 6mm; height: 0.25mm; background: #2563eb; transform: translate(-50%, -50%); }
    .cross-v { position: absolute; left: 50%; top: 50%; width: 0.25mm; height: 6mm; background: #2563eb; transform: translate(-50%, -50%); }
    .idx { position: absolute; left: 1mm; top: 0.8mm; font-size: 6pt; color: #64748b; font-family: ui-monospace, monospace; }
    .hint { position: absolute; left: 6mm; top: 4mm; font-size: 8pt; color: #475569; }
    @media screen { body { padding: 10mm; background: #eef1f4; } .page { background:#fff; box-shadow:0 2px 12px rgba(0,0,0,.15); margin:0 auto; } }`;
  doc.head.appendChild(style);

  const page = doc.createElement('div');
  page.className = 'page';

  const hint = doc.createElement('div');
  hint.className = 'hint';
  hint.textContent = lang === 'en'
    ? `Calibration — offset X ${offsetX} mm / Y ${offsetY} mm · ${gCols}×${gRows}`
    : `Kalibrierung — Versatz X ${offsetX} mm / Y ${offsetY} mm · ${gCols}×${gRows}`;
  page.appendChild(hint);

  for (let r = 0; r < gRows; r++) {
    for (let c = 0; c < gCols; c++) {
      const left = (useExact ? (exact.x0 + c * exact.dx) : (margin + c * (cellW + gap))) + offsetX;
      const top = (useExact ? (exact.y0 + r * exact.dy) : (margin + r * (cellH + gap))) + offsetY;
      const cell = doc.createElement('div');
      cell.className = 'cell';
      cell.style.left = `${left}mm`;
      cell.style.top = `${top}mm`;
      cell.style.width = `${cellW}mm`;
      cell.style.height = `${cellH}mm`;

      // corner ticks (L-shapes) at the four corners
      const corners = [
        { x: 0, y: 0, hx: 0, hy: 0 },
        { x: cellW, y: 0, hx: -4, hy: 0 },
        { x: 0, y: cellH, hx: 0, hy: -0.3 },
        { x: cellW, y: cellH, hx: -4, hy: -0.3 },
      ];
      corners.forEach((corner) => {
        const h = doc.createElement('div');
        h.className = 'tick t-h';
        h.style.left = `${corner.x + corner.hx}mm`;
        h.style.top = `${corner.y === cellH ? corner.y - 0.3 : corner.y}mm`;
        cell.appendChild(h);
        const v = doc.createElement('div');
        v.className = 'tick t-v';
        v.style.left = `${corner.x === cellW ? corner.x - 0.3 : corner.x}mm`;
        v.style.top = `${corner.y === cellH ? corner.y - 4 : corner.y}mm`;
        cell.appendChild(v);
      });

      const ch = doc.createElement('div'); ch.className = 'cross-h'; cell.appendChild(ch);
      const cv = doc.createElement('div'); cv.className = 'cross-v'; cell.appendChild(cv);
      const idx = doc.createElement('div'); idx.className = 'idx'; idx.textContent = String(r * gCols + c + 1); cell.appendChild(idx);

      page.appendChild(cell);
    }
  }
  doc.body.appendChild(page);
  setTimeout(() => { win.focus(); win.print(); }, 250);
}


export function parseLabelLines(text) {
  // Each line: value[,caption[,quantity]]
  return (text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(',').map((p) => p.trim());
      const value = parts[0] || '';
      const caption = parts[1] || '';
      let quantity = parseInt(parts[2], 10);
      if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
      return { value, caption, quantity: Math.min(200, quantity) };
    })
    .filter((x) => x.value.length > 0);
}

export function rowsToLabelText(rows) {
  // rows: array of arrays (columns). Column 0=value, 1=caption, 2=quantity.
  let data = (rows || [])
    .map((r) => (Array.isArray(r) ? r.map((c) => (c == null ? '' : String(c).trim())) : []))
    .filter((r) => r.length && (r[0] || '').length >= 0);
  data = data.filter((r) => r.some((c) => c && c.length));
  if (!data.length) return '';
  const first = data[0];
  const firstQty = first.length > 2 ? parseInt(first[2], 10) : NaN;
  const looksHeader = data.length > 1 && !Number.isFinite(firstQty) &&
    /^[a-zA-Z_ äöüÄÖÜ]+$/.test(first[0] || '') && (first[0] || '').length < 20;
  if (looksHeader) data = data.slice(1);
  return data
    .filter((r) => (r[0] || '').length > 0)
    .map((r) => {
      const value = r[0] || '';
      const caption = r[1] || '';
      let qty = parseInt(r[2], 10);
      if (!Number.isFinite(qty) || qty < 1) qty = 1;
      const parts = [value];
      if (caption || qty > 1) parts.push(caption);
      if (qty > 1) parts.push(String(Math.min(200, qty)));
      return parts.join(', ');
    })
    .join('\n');
}

export function parseLabelCsv(text) {
  const lines = (text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const rows = lines.map((line) => line.split(',').map((c) => c.trim().replace(/^"|"$/g, '')));
  return rowsToLabelText(rows);
}

// Parse raw CSV text into an array of arrays (no header detection / no conversion).
export function parseCsvRows(text) {
  const delim = ((text || '').indexOf(';') > -1 && (text || '').indexOf(',') === -1) ? ';' : ',';
  return (text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => line.split(delim).map((c) => c.trim().replace(/^"|"$/g, '')));
}

// Build the label textarea content from raw rows using an explicit column mapping.
// map: { value, caption, quantity, skipHeader } where value/caption/quantity are
// column indices (or -1 / null to ignore).
export function buildLabelTextFromMapping(rows, map) {
  if (!Array.isArray(rows) || !rows.length) return '';
  const vCol = Number.isInteger(map.value) ? map.value : 0;
  const cCol = Number.isInteger(map.caption) ? map.caption : -1;
  const qCol = Number.isInteger(map.quantity) ? map.quantity : -1;
  const data = map.skipHeader && rows.length > 1 ? rows.slice(1) : rows;
  return data
    .map((r) => (r[vCol] || '').trim())
    .map((_, i) => {
      const r = data[i];
      const value = (r[vCol] || '').trim();
      if (!value) return null;
      const caption = cCol >= 0 ? (r[cCol] || '').trim() : '';
      let qty = qCol >= 0 ? parseInt(r[qCol], 10) : 1;
      if (!Number.isFinite(qty) || qty < 1) qty = 1;
      const parts = [value];
      if (caption || qty > 1) parts.push(caption);
      if (qty > 1) parts.push(String(Math.min(200, qty)));
      return parts.join(', ');
    })
    .filter(Boolean)
    .join('\n');
}

export function downloadJson(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function safeName(str, fallback = 'barcode') {
  const s = (str || '').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 40);
  return s || fallback;
}

export function parseListInput(text) {
  return (text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
}

export function parseCsv(text) {
  const lines = (text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  // Take first column of each line
  const rows = lines.map((line) => {
    const first = line.split(',')[0].trim().replace(/^"|"$/g, '');
    return first;
  });
  // Auto-skip a header row if it looks non-numeric label like 'value' etc.
  if (rows.length > 1 && /^[a-zA-Z_ ]+$/.test(rows[0]) && rows[0].length < 20) {
    const rest = rows.slice(1);
    if (rest.some((r) => r.length)) return rest.filter(Boolean);
  }
  return rows.filter(Boolean);
}
